import { minBy, maxBy, get, orderBy, isObject, isArray } from 'lodash';

import dayjs from 'services/dayjs';

export const groupItemsByDate = (
  items,
  selectDatePath,
  dateFormat = 'YYYY-MM',
) => {
  const dates = [];
  const minDate = dayjs(
    get(
      minBy(items, i => get(i, selectDatePath)),
      selectDatePath,
    ),
  );
  const maxDate = dayjs(
    get(
      maxBy(items, i => get(i, selectDatePath)),
      selectDatePath,
    ),
  );
  if (minDate.format(dateFormat) === maxDate.format(dateFormat)) {
    return [
      {
        items: orderBy(items, i => new Date(get(i, selectDatePath))),
        year: minDate.format('YYYY'),
        month: minDate.format('MMMM'),
      },
    ];
  }
  let date = minDate;

  while (date.format(dateFormat) !== maxDate.format(dateFormat)) {
    dates.push(date.format(dateFormat));
    date = date.add(1, 'month');
  }

  dates.push(maxDate.format(dateFormat));

  return dates.map(d => ({
    items: orderBy(
      items.filter(i => dayjs(get(i, selectDatePath)).format(dateFormat) === d),
      i => new Date(get(i, selectDatePath)),
    ),
    year: dayjs(d).format('YYYY'),
    month: dayjs(d).format('MMMM'),
  }));
};

export const addOrRemove = (array, item) => {
  const exists = array.includes(item);
  if (exists) {
    return array.filter(c => c !== item);
  }
  return [...array, item];
};

/**
 * Checks that the given array is valid and not empty
 *
 * @param {Array} arr - Array
 * @returns {Boolean}
 */
export const isNonEmptyArray = arr => Array.isArray(arr) && arr.length > 0;

/**
 * Ensures the object is an array.
 *
 * @param {*} obj
 * @returns {Array}
 */
export const ensureArray = obj => {
  if (!obj) {
    return [];
  }
  return Array.isArray(obj) ? obj : [obj];
};

/**
 * Ensure the object is an object
 * @param {Object} obj
 * @returns {Object}
 */
export const ensureObject = obj => (isObject(obj) && !isArray(obj) ? obj : {});

/**
 * Returns all possible combinations for an array of values
 * @param {Array} inputArr
 * @returns {Array}
 */
export const combinations = inputArr =>
  inputArr.flatMap((v, i) => inputArr.slice(i + 1).map(w => [v, w]));

/**
 * Returns elements joined by , with the last one joined by and.
 * @param {Array} array
 * @returns str
 */
export const conjunctionJoin = array => {
  const last = array.pop();
  return `${array.join(', ')} and ${last}`;
};

/**
 * Find an item from the array and toggles it's value
 * @param {Array} arr
 * @param {String | Number} item
 * @returns Array
 */
export const findAndToggleItem = (arr, item) => {
  if (!Array.isArray(arr)) {
    return [];
  }
  const i = arr.findIndex(el => el === item);
  return i === -1 ? [...arr, item] : [...arr.slice(0, i), ...arr.slice(i + 1)];
};

/**
 * Check if at least one element of values is included in array
 * @example
 * // returns true
 * includesAny(['completed', 'in_progress', 'failed'], ['completed', 'failed'])
 * @param {Array} arr
 * @param {Array} values
 * @returns boolean
 */
export const includesAny = (arr, values) => values.some(v => arr.includes(v));

/**
 * i.e. const array = [...conditionalArray(condition, arr)];
 * @param condition {bool}
 * @param arrOne {array}
 * @param arrTwo {array} - optional
 * @returns {array}
 */
export const conditionalArray = (condition, arrOne, arrTwo = []) =>
  condition ? arrOne : arrTwo;

/**
 *
 * @param {Array} arr
 * @returns ids of the array
 */
export const getIdsFromArray = arr => ensureArray(arr).map(({ id }) => id);
